import React, { useEffect } from "react";
import { View, Text, FlatList, Image } from "react-native";
import { CreateResponsiveStyle, DEVICE_SIZES } from "rn-responsive-styles";
import { AppColors } from "../utility/AppColors";
import { AppImages } from "../utility/AppImages";
import { AppFontStyle } from "../styles/AppFontStyle";
import { ChatData, useChat } from "../context/ChatContext";
import { LinearGradient } from "expo-linear-gradient";

interface MessageItemProps {
  message: {
    messageContent: string;
    userId: string;
    timestamp: number;
  };
  isUser: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, isUser }) => {
  const styles = useStyles();

  const formattedTime = new Date(message.timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <LinearGradient
      colors={["#1F2937", "#111827"]}
      start={[0, 0]}
      end={[0, 1]}
      style={[
        styles.messageContainer,
        {
          alignSelf: isUser ? "flex-end" : "flex-start",
          borderRadius: 12,
          borderTopRightRadius: isUser ? 0 : 12,
          borderBottomLeftRadius: isUser ? 12 : 0,
          borderWidth: 0.8,
          borderColor: isUser ? "#DC2626" : "#FFFFFF",
        },
      ]}
    >
      <View
        style={[
          styles.header,
          { justifyContent: isUser ? "flex-end" : "space-between" },
        ]}
      >
        {!isUser && (
          <Text
            style={[styles.userNameText, { color: "#FFFFFF" }]}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {message.userId}
          </Text>
        )}
        <Text style={[styles.timestampText, { color: "#FFFFFF" }]}>
          {formattedTime}
        </Text>
      </View>
      <Text style={[styles.adminMessageText, { color: "#FFFFFF" }]}>
        {message.messageContent}
      </Text>
    </LinearGradient>
  );
};

interface MessageListProps {
  messages: any;
  flatListRef: React.RefObject<
    FlatList<{ messageContent: string; userId: string; timestamp: any }>
  >;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  flatListRef,
}) => {
  const { userData } = useChat();

  const renderItem = ({
    item,
  }: {
    item: { messageContent: string; userId: string; timestamp: any };
  }) => (
    <MessageItem message={item} isUser={item.userId === userData.username} />
  );

  useEffect(() => {
    if (flatListRef.current) {
      const timeout = setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [messages]);

  return (
    <FlatList
      data={messages}
      renderItem={renderItem}
      keyExtractor={(item, index) => index.toString()}
      contentContainerStyle={{ padding: 10 }}
      ref={flatListRef}
      ListEmptyComponent={<NoMessages deviceSize={DEVICE_SIZES.MD} />}
    />
  );
};

interface NoMessagesProps {
  deviceSize: DEVICE_SIZES;
  pageType?: string;
  isSignIn?: Boolean;
  zeroMessage?: boolean;
}

export const NoMessages: React.FC<NoMessagesProps> = ({
  deviceSize,
  pageType = "",
  isSignIn,
  zeroMessage,
}) => (
  <View>
    <View
      style={{
        marginTop: "25%",
        marginBottom: pageType === "event" && !isSignIn ? "15%" : "25%",
      }}
    >
      <Image
        source={AppImages.icMessage}
        style={{
          height: 27,
          width: 30,
          justifyContent: "center",
          alignSelf: "center",
        }}
      />
      {zeroMessage && (
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.BOLD_ANYBODY_18
              : AppFontStyle.REGULAR_ANYBODY_16,
            {
              marginTop: 15,
              justifyContent: "center",
              alignSelf: "center",
              color: AppColors.gray_C3C3C3,
            },
          ]}
        >
          Be the first to send a message
        </Text>
      )}
      {pageType === "event" ? (
        <View
          style={{
            paddingHorizontal:
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 20 : 10,
            marginTop: 15,
            justifyContent: "center",
            alignSelf: "center",
          }}
        >
          {isSignIn ? (
            <Text
              style={[
                deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                  ? AppFontStyle.REGULAR_ANYBODY_14
                  : AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3, textAlign: "center" },
              ]}
            >
              <Text style={{ fontWeight: "bold" }}>Note:</Text> You can only
              send and view messages in the event room after signing-up for the
              event.
            </Text>
          ) : (
            <Text
              style={[
                deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                  ? AppFontStyle.REGULAR_ANYBODY_14
                  : AppFontStyle.REGULAR_ANYBODY_12,
                { color: AppColors.gray_C3C3C3, textAlign: "center" },
              ]}
            >
              You can now send messages and join the conversation with other
              participants. Feel free to chat, share thoughts, and enjoy the
              event together!
            </Text>
          )}
        </View>
      ) : (
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.REGULAR_ANYBODY_14
              : AppFontStyle.REGULAR_ANYBODY_12,
            {
              marginTop: 15,
              justifyContent: "center",
              alignSelf: "center",
              color: AppColors.gray_C3C3C3,
            },
          ]}
        >
          Send a message to let other players know what you say
        </Text>
      )}
    </View>
    {pageType === "event" && !isSignIn ? (
      <Text
        style={[
          AppFontStyle.REGULAR_ANYBODY_12,
          { color: AppColors.gray_C3C3C3 },
        ]}
      >
        Please remember to communicate respectfully and keep the chat
        appropriate for all users.
      </Text>
    ) : null}
  </View>
);

interface CancelEventMessageProps {
  deviceSize: DEVICE_SIZES;
  pageType?: string;
  isSignIn?: Boolean;
  reason?: string;
}
export const CancelEventMessage: React.FC<CancelEventMessageProps> = ({
  deviceSize,
  pageType = "",
  isSignIn,
  reason = "",
}) => (
  <View>
    <View
      style={{
        marginTop: "25%",
        marginBottom: pageType === "event" && !isSignIn ? "15%" : "25%",
      }}
    >
      <Image
        source={AppImages.icMessage}
        style={{
          height: 27,
          width: 30,
          justifyContent: "center",
          alignSelf: "center",
        }}
      />
      <Text
        style={[
          deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
            ? AppFontStyle.BOLD_ANYBODY_18
            : AppFontStyle.REGULAR_ANYBODY_16,
          {
            marginTop: 15,
            justifyContent: "center",
            alignSelf: "center",
            color: AppColors.gray_C3C3C3,
          },
        ]}
      >
        Event canceled
      </Text>
      <View
        style={{
          paddingHorizontal:
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE ? 20 : 10,
          marginTop: 15,
          justifyContent: "center",
          alignSelf: "center",
        }}
      >
        <Text
          style={[
            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
              ? AppFontStyle.REGULAR_ANYBODY_14
              : AppFontStyle.REGULAR_ANYBODY_12,
            { color: AppColors.gray_C3C3C3, textAlign: "center" },
          ]}
        >
          {reason}
        </Text>
      </View>
    </View>
  </View>
);

const useStyles = CreateResponsiveStyle(
  {
    eventView: {
      marginTop: 5,
      marginBottom: 5,
      alignSelf: "flex-end",
    },
    messageContainer: {
      padding: 10,
      borderRadius: 10,
      marginVertical: 5,
      minWidth: "40%",
      maxWidth: "60%",
    },
    userMessageText: {
      color: "#000000",
      fontSize: 16,
      fontWeight: "400",
    },
    adminMessageText: {
      fontSize: 16,
      fontWeight: "400",
      color: "#FFFFFF",
    },
    header: {
      flexDirection: "row",
      marginBottom: 5,
    },
    userNameText: {
      fontWeight: "bold",
      color: AppColors.black,
      maxWidth: "60%",
    },
    timestampText: {
      fontSize: 12,
      color: AppColors.gray_C3C3C3,
    },
    chatBox: {
      marginTop: 30,
      position: "absolute",
      right: 0,
      bottom: -150,
    },
    chatOuterView: {
      margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 10,
      marginVertical: 10,
    },
    btnChat: {
      paddingVertical: 0,
      paddingHorizontal: 15,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      marginTop: 10,
      borderRadius: 7,
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      chatBox: {
        marginTop: 30,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
    [DEVICE_SIZES.MD]: {
      chatBox: {
        marginTop: 30,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
    [DEVICE_SIZES.SM]: {
      chatBox: {
        marginTop: 30,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
    [DEVICE_SIZES.XS]: {
      chatBox: {
        marginTop: 30,
        position: "relative",
        right: 0,
        bottom: -0,
      },
    },
  }
);
